import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { WebSocketNotificationTypeEnum } from 'src/app/core/websocket';
import { WebSocketService } from 'src/app/core/websocket/websocket.service';
import { BasicService } from 'src/app/shared/services/basic.service';
import { SuggestAnswerService } from 'src/app/shared/services/suggest-answer.service';
import { environment } from 'src/environments/environment';

import { TaskProcessContext } from '../shared/task-process-context.class';
import { AiSummaryService } from './ai-summary.service';
import { AiSummaryStatusEnum } from './enums';

@Component({
  selector: 'app-ai-summary',
  templateUrl: './ai-summary.component.html',
  styleUrls: ['./ai-summary.component.scss'],
})
export class AiSummaryComponent implements OnInit, OnDestroy {
  @Input() taskProcessContext: TaskProcessContext;
  @Input() nbMessages: number;
  @Input() isInBottom: boolean;

  showSummary = false;
  isLoading = false;
  summary = '';
  readonly NB_MESSAGES_MIN = 4;

  private subscriptions = new Subscription();

  constructor(
    private basicService: BasicService,
    private suggestAnswerService: SuggestAnswerService,
    private webSocketService: WebSocketService,
    private aiSummaryService: AiSummaryService
  ) {}

  ngOnInit(): void {
    if (!environment.isNativeApp && !this.taskProcessContext.engagement && this.nbMessages >= this.NB_MESSAGES_MIN) {
      this.subscriptions.add(
        this.suggestAnswerService.AissitantConfigForTeamBS.subscribe(resp => {
          if (resp.aiAgentId && resp.aiSummary) {
            this.showSummary = true;
            this.isLoading = true;
            this.listenAiSummaryFromWS();
            this.aiSummaryService.getAiSummary(this.taskProcessContext.processedTaskId);

            if (this.taskProcessContext.isTheLastFromUser) {
              this.taskProcessContext.scrollToBottom();
            } else {
              this.taskProcessContext.scrollToProcessedTask();
            }
          } else {
            this.showSummary = false;
          }
        })
      );
    }
  }

  private listenAiSummaryFromWS() {
    this.subscriptions.add(
      this.webSocketService.message$
        .pipe(
          filter(
            (event: any) =>
              event.type === WebSocketNotificationTypeEnum.AIAssistantSummary &&
              event.task_id === this.taskProcessContext.processedTaskId
          )
        )
        .subscribe({
          next: event => {
            const { data, task_id: taskId } = event;

            if (data.status !== AiSummaryStatusEnum.STARTED && this.aiSummaryService.summaryPending[taskId]) {
              this.isLoading = false;
              this.aiSummaryService.summaryPending[taskId].received = true;
              this.summary = data.summary ? data.summary : this.basicService.translate('m/task-process/summary/error');

              if (data.status === AiSummaryStatusEnum.EXPIRED) {
                this.aiSummaryService.summaryPending[taskId].expiredSubscription.unsubscribe();
                this.aiSummaryService.summaryPending[taskId] = null;
              }

              if (this.taskProcessContext.isTheLastFromUser) {
                this.taskProcessContext.scrollToBottom();
              } else {
                this.taskProcessContext.scrollToProcessedTask();
              }
            }
          },
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();

    // if coming back on same task before expiration delay (jwtValidityInMs), it could trigger 'EXPIRED' of first call before getting the response of 2nd call
    if (this.aiSummaryService.summaryPending[this.taskProcessContext.processedTaskId]) {
      this.aiSummaryService.summaryPending[this.taskProcessContext.processedTaskId].expiredSubscription.unsubscribe();
      this.aiSummaryService.summaryPending[this.taskProcessContext.processedTaskId] = null;
    }
  }
}
